const listeners = new Set;

let can_scroll = true;

const keyCodes = {
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
    SPACE: 32
}

function isTrackpad(event) {
    const perf = performance.now() - event.timeStamp;
    const delta = Math.abs( event.wheelDelta );
    const x = Math.abs( event.deltaX );
    const y = Math.abs( event.deltaY );

    return ( x > 0 && y > 0  ) || ( perf >= 17 && delta < 60 );
}

class SmoothManager {
    wrapper;

    registered = false;

    options = {
        mouseMultiplier: 1,
        trackpadMultiplier: 0.5,
        touchMultiplier: 20,
        touchInertiaMultiplier: 0,
        firefoxMultiplier: 15,
        windowsMultiplier: 1,
        macosMultiplier: 1,
        linuxMultiplier: 1,
        keyboardStep: 1,
        ease: 0.15,
        touchEase: 0.4,
        useKeyboard: true,
        useTouch: true,
        preventTouch: false,
        wrapper: document.body,
    };

    support = {
        hasWheelEvent: 'onwheel' in document,
        hasMouseWheelEvent: 'onmousewheel' in document,
        hasTouch: 'ontouchstart' in document,
        hasKeyDown: 'onkeydown' in document,
        isFirefox: navigator.userAgent.indexOf('Firefox') > -1,
        isMacOS: navigator.userAgent.indexOf('Macintosh') > -1,
        isWindows: navigator.userAgent.indexOf('Windows') > -1,
    };

    touchStart = {
        x: 0,
        y: 0,
    };

    lastTouch = {
        x: 0,
        y: 0,
    };

    animatedDeltaX = 0;
    animatedDeltaY = 0;

    rafId = null;

    startTime = null;

    lastEvent = null;

    isTouchActive = false;

    register(newOptions = {}) {
        this.options = Object.assign( {}, this.options, newOptions );

        if ( this.registered ) {
            return;
        }

        this.wrapper = this.options.wrapper;

        if ( this.support.hasWheelEvent || this.support.hasMouseWheelEvent ) {
            const name = 'onwheel' in document ? 'wheel' : 'mousewheel';

            this.wrapper.addEventListener( name, this._onWheel );
        }

        if ( this.support.hasKeyDown && this.options.useKeyboard ) {
            this.wrapper.addEventListener( 'keydown', this._onKeyDown );
        }

        if ( this.support.hasTouch && this.options.useTouch ) {
            this.wrapper.addEventListener( 'touchstart', this._onTouchStart );

            this.wrapper.addEventListener( 'touchmove', this._onTouchMove, { passive: !this.options.preventTouch } );

            this.wrapper.addEventListener( 'touchend', this._onTouchEnd );
        }

        this.registered = true;
    }

    unregister() {
        if ( ! this.registered ) {
            return;
        }

        if ( this.support.hasWheelEvent || this.support.hasMouseWheelEvent ) {
            const name = 'onwheel' in document ? 'wheel' : 'mousewheel';

            this.wrapper.removeEventListener( name, this._onWheel );
        }

        if ( this.support.hasKeyDown && this.options.useKeyboard ) {
            this.wrapper.removeEventListener( 'keydown', this._onKeyDown );
        }

        if ( this.support.hasTouch && this.options.useTouch ) {
            this.wrapper.removeEventListener( 'touchstart', this._onTouchStart );

            this.wrapper.removeEventListener( 'touchmove', this._onTouchMove, { passive: ! this.options.preventTouch } );

            this.wrapper.removeEventListener( 'touchend', this._onTouchEnd );
        }

        if ( this.rafId ) {
            cancelAnimationFrame( this.rafId );
        }

        this.wrapper = undefined;

        this.registered = false;
    }

    _onWheel = event => {
        let deltaX = event.wheelDeltaX || event.deltaX * -1;
        let deltaY = event.wheelDeltaY || event.deltaY * -1;

        if ( this.support.isFirefox && event.deltaMode === 1 ) {
            deltaX *= this.options.firefoxMultiplier;
            deltaY *= this.options.firefoxMultiplier;
        }

        if ( isTrackpad( event ) ) {
            deltaX *= this.options.trackpadMultiplier;
            deltaY *= this.options.trackpadMultiplier;
        } else {
            deltaX *= this.options.mouseMultiplier;
            deltaY *= this.options.mouseMultiplier;
        }

        if ( this.support.isWindows ) {
            deltaX *= this.options.windowsMultiplier;
            deltaY *= this.options.windowsMultiplier;
        } else if ( this.support.isMacOS ) {
            deltaX *= this.options.macosMultiplier;
            deltaY *= this.options.macosMultiplier;
        } else {
            deltaX *= this.options.linuxMultiplier;
            deltaY *= this.options.linuxMultiplier;
        }

        this.update( deltaX, deltaY, event );
    }

    _onKeyDown = event => {
        let deltaX = 0;
        let deltaY = 0;

        switch (event.keyCode) {
            case keyCodes.LEFT:
            case keyCodes.UP:
                deltaY = this.options.keyboardStep;
                break;

            case keyCodes.RIGHT:
            case keyCodes.DOWN:
                deltaY = this.options.keyboardStep * -1;
                break;
            case keyCodes.SPACE:
                deltaY = ( this.options.keyboardStep * 5 ) * (event.shiftKey ? 1 : -1);
                break;
            default:
                return;
        }

        this.update( deltaX, deltaY, event );
    }

    _onTouchStart = event => {
        this.isTouchActive = true;

        const touch = event.targetTouches ? event.targetTouches[0] : event;

        this.touchStart.x = touch.pageX;
        this.touchStart.y = touch.pageY;
        this.lastTouch.x = 0;
        this.lastTouch.y = 0;
    }

    _onTouchMove = event => {
        if ( this.options.preventTouch ) {
            event.preventDefault();
        }

        const touch = event.targetTouches ? event.targetTouches[0] : event;

        const deltaX = (touch.pageX - this.touchStart.x) * this.options.touchMultiplier;
        const deltaY = (touch.pageY - this.touchStart.y) * this.options.touchMultiplier;

        this.touchStart.x = touch.pageX;
        this.touchStart.y = touch.pageY;

        this.lastTouch.x = deltaX;
        this.lastTouch.y = deltaY;

        this.update( deltaX, deltaY, event );
    }

    _onTouchEnd = () => {
        this.isTouchActive = false;

        if ( this.lastTouch.x || this.lastTouch.y ) {
            const inertiaX = this.lastTouch.x * this.options.touchInertiaMultiplier;
            const inertiaY = this.lastTouch.y * this.options.touchInertiaMultiplier;

            this.update( inertiaX, inertiaY, this.lastEvent );
        }

        this.touchStart.x = 0;
        this.touchStart.y = 0;
        this.lastTouch.x = 0;
        this.lastTouch.y = 0;
    }

    update(deltaX, deltaY, event) {
        if ( ! can_scroll ) {
            return;
        }

        this.animatedDeltaX += deltaX;
        this.animatedDeltaY += deltaY;
        this.startTime = performance.now();
        this.lastEvent = event;

        if ( ! this.rafId ) {
            this.rafId = requestAnimationFrame( this._loop );
        }
    }

    _loop = timestamp => {
        const activeEase = this.isTouchActive ? this.options.touchEase : this.options.ease;

        const deltaX = this.animatedDeltaX * activeEase;
        const deltaY = this.animatedDeltaY * activeEase;

        this.animatedDeltaX -= deltaX;
        this.animatedDeltaY -= deltaY;

        const time = timestamp - this.startTime;

        if ( Math.abs( this.animatedDeltaX ) < 0.1 && Math.abs( this.animatedDeltaY ) < 0.1) {
            this.notify({ deltaX: this.animatedDeltaX, deltaY: this.animatedDeltaY, time, event: this.lastEvent });
            this.animatedDeltaX = 0;
            this.animatedDeltaY = 0;
            this.rafId = null;
            this.startTime = null;
            return;
        }

        this.notify({ deltaX, deltaY, time, event: this.lastEvent });

        this.rafId = requestAnimationFrame( this._loop );
    }

    notify(args) {
        listeners.forEach( callback => callback( args ) );

        return this;
    }

    lockScroll(lock_scroll = true) {
        can_scroll = ! lock_scroll;

        return this;
    }

    unlockScroll() {
        return this.lockScroll( false );
    }

    add(callback) {
        listeners.add( callback );

        return this;
    }

    remove(callback) {
        listeners.delete( callback );

        return this;
    }
}

const smoothManager = new SmoothManager;

export default smoothManager;

export {
    smoothManager
}